/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import API from '@apps/nnc/services/nnc';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import {
  NncSchemaType,
  AnalysisFormSchema,
  AnalysisFormType,
} from '@apps/nnc/schemas/nnc';
import OriginAPI from '@apps/nnc/services/origin';
import CauseAPI from '@apps/nnc/services/cause';

import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Stack,
  TextField,
  Paper,
  Collapse,
  Modal,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import NNCIcon from '@/icons/nnc';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import { PageTitle, Spinner } from '@/components/atoms';
import { enqueueSnackbar } from 'notistack';
import DeleteIcon from '@assets/global/delete.svg';
import {
  DataGrid,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';

const formatBody = (str: string | undefined): number[] => {
  if (!str) return [];
  return str.includes('-')
    ? str.split('-').map((i) => Number(i))
    : [Number(str)];
};

const NNCAnalysisForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Record<number, boolean>>(
    {},
  );
  const [open, setOpen] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);

  const [modalData, setModalData] = useState<any>();
  const [successReferenceNumber, setSuccessReferenceNumber] = useState<
    string | null
  >(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const navigate = useNavigate();
  const toggleDropdown = (id: number) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const { data, isLoading, error } = useQuery<{ data: NncSchemaType[] }, Error>(
    {
      queryKey: ['entries', rowSelectionModel],
      queryFn: () => API.detail('entries', { ids: [...rowSelectionModel] }),
      enabled: rowSelectionModel.length > 0,
    },
  );

  const form = useForm<AnalysisFormType>({
    resolver: zodResolver(AnalysisFormSchema),
    defaultValues: {
      comment_general: '',
      modos: [],
    },
  });

  const { fields, remove, prepend } = useFieldArray({
    control: form.control,
    name: 'modos',
  });

  const handleAddCause = () => {
    prepend({ origin: undefined, cause: undefined, comment: undefined });
  };

  const handleOpenModal = async () => {
    const response = await API.list({
      status: 3,
      unfounded: false,
      unit: data?.data?.[0]?.unit?.id || null,
    });
    setModalData(response.data.results);
    setOpen(true);
  };

  const handleSubmit = async (data: AnalysisFormType) => {
    try {
      setLoading(true);
      const response = await API.critical_analysis({
        nnc_ids: rowSelectionModel,
        ...data,
      });

      setSuccessReferenceNumber(response.data.reference_number);
      setShowSuccessMessage(true);
      setLoading(false);

      enqueueSnackbar(t('Cause Analysis created successfully!'), {
        variant: 'success',
      });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(t('Error when trying to create Cause Analysis'), {
        variant: 'error',
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(form.formState.errors).length > 0) {
      console.log('Zod validation errors:', form.formState.errors);
    }
  }, [form.formState.errors]);

  useEffect(() => {
    const ids = formatBody(id);
    setRowSelectionModel(ids);
  }, [id]);

  const handleContinue = () => {
    navigate('/nnc/entries');
  };

  if (error) {
    return (
      <Box>
        <Typography color="error">
          Erro ao carregar dados: {(error as Error).message}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <PageTitle title={t('NNC Entries')} />
      <Box sx={{ p: 2, minHeight: '100vh' }}>
        <Spinner open={isLoading || loading} />

        {showSuccessMessage && successReferenceNumber && (
          <Card
            elevation={4}
            sx={{ borderRadius: 4, mb: 3, textAlign: 'center' }}
          >
            <CardContent>
              <Box display="flex" justifyContent="center" mb={2}>
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main' }} />
              </Box>
              <Typography variant="h5" color="success.main" gutterBottom>
                {t('Cause Analysis created successfully!')}
              </Typography>
              <Typography variant="h6" sx={{ mt: 2, mb: 3 }}>
                {t('Reference Number')}:{' '}
                <strong>{successReferenceNumber}</strong>
              </Typography>
              <Button
                variant="gradient"
                color="primary"
                size="large"
                onClick={handleContinue}
                sx={{ mt: 2 }}
              >
                {t('Continue')}
              </Button>
            </CardContent>
          </Card>
        )}

        {!showSuccessMessage && (
          <FormContainer formContext={form} onSuccess={handleSubmit}>
            <Box mb={3} display={'flex'} justifyContent={'flex-end'}>
              <Button
                onClick={() => {
                  handleOpenModal();
                }}
              >
                + {t('Include Another NNC')}
              </Button>
            </Box>
            <Stack spacing={2}>
              {/* NNC IDs display */}

              {data?.data?.map((item: NncSchemaType) => (
                <Paper
                  elevation={3}
                  sx={{ padding: 2, borderRadius: 4 }}
                  key={`dropdown-${item.id}`}
                >
                  <Box
                    display={'flex'}
                    justifyContent="space-between"
                    alignItems={'center'}
                  >
                    <Stack direction="row" spacing={1} alignItems={'center'}>
                      <Box
                        sx={{
                          background: '#00d160',
                          padding: 1,
                          borderRadius: '30%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <NNCIcon sx={{ color: 'white !important' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 22,
                        }}
                      >
                        {item.reference_number}
                      </Typography>
                    </Stack>

                    <Button onClick={() => toggleDropdown(item.id)}>
                      {openDropdowns[item.id] ? '▲' : '▼'}
                    </Button>
                  </Box>
                  <Collapse in={openDropdowns[item.id]}>
                    <Grid
                      container
                      spacing={2}
                      my={2}
                      sx={{ borderTop: '1px solid #B3BCCC' }}
                      pt={2}
                    >
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Typography>SAP ZNCR</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          disabled
                          value={item.country}
                          label={t('Country')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          value={item.unit.label}
                          disabled
                          name="unit.label"
                          label={t('Unit')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          value={item.nnc_date}
                          disabled
                          name="nnc_date"
                          label={t('NNC Date')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          value={item.product}
                          disabled
                          name="product"
                          label={t('Product')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          value={item.production_order}
                          disabled
                          name="production_order"
                          label={t('Production Order')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          value={item.operation}
                          disabled
                          name="operation"
                          label={t('Operation')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          value={item.operator_name}
                          disabled
                          name="operator_name"
                          label={t('Operator Generator')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          value={item.machine}
                          disabled
                          name="machine"
                          label={t('Machine')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          value={item.failure_category_description}
                          disabled
                          name="failure_category_description"
                          label={t('Failure Category Description')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <TextField
                          value={item.description}
                          disabled
                          name="description"
                          label={t('Description')}
                          fullWidth
                          required
                          multiline
                          minRows={2}
                        />
                      </Grid>

                      <Grid mt={3} size={{ xs: 12, md: 12 }}>
                        <Typography>{t('NNC Initial Details')}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          value={item.division?.label}
                          name="division"
                          label={t('Identified Area')}
                          fullWidth
                          required
                          disabled
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          value={item.production_phase?.label}
                          name="production_phase"
                          label={t('Production Phase')}
                          fullWidth
                          required
                          disabled
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          value={item.machine_enrichment?.label}
                          name="machine_enrichment"
                          label={t('NNC Machine')}
                          fullWidth
                          required
                          disabled
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField
                          disabled
                          value={item.operator_code}
                          name="operator_code"
                          label={t('Operator Cod.')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          disabled
                          value={item.operator_name_enrichment}
                          name="operator_name_enrichment"
                          label={t('Operator Generator')}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid mb={3} size={{ xs: 12, md: 12 }}>
                        <TextField
                          value={item.comment}
                          disabled
                          name="comment"
                          label={t('NNC Comment')}
                          fullWidth
                          required
                          multiline
                          minRows={2}
                        />
                      </Grid>
                      {data?.data?.length > 1 && (
                        <Grid
                          size={{ xs: 12, md: 12 }}
                          display={'flex'}
                          justifyContent="flex-end"
                        >
                          <Button
                            sx={{
                              background: '#2D354A',
                              padding: '16px',
                              fontSize: '18px',
                            }}
                            onClick={() => {
                              setRowSelectionModel((prev) =>
                                prev.filter((i) => i !== item.id),
                              );
                            }}
                          >
                            {t('Remove NNC')} <CloseIcon sx={{ ml: 1 }} />
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Collapse>
                </Paper>
              ))}

              {/* General Comment */}
              <Card elevation={4} sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('General Comment')}
                  </Typography>
                  <TextFieldElement
                    name="comment_general"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </CardContent>
              </Card>

              {/* Failure Mode Identification */}
              <Card elevation={4} sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    pt={1}
                  >
                    <Typography variant="subtitle1" fontSize={18}>
                      {fields.length === 0
                        ? t(
                            'You have not yet performed any root cause analysis.',
                          )
                        : t('Failure Mode Identification')}
                    </Typography>
                    <Button
                      variant="contained"
                      endIcon={<AddIcon />}
                      onClick={handleAddCause}
                      color="secondary"
                      sx={{ py: 2 }}
                    >
                      {t('Add Cause')}
                    </Button>
                  </Box>

                  {fields.map((field, index) => (
                    <Grid container spacing={2} key={field.id} sx={{ mt: 2 }}>
                      <Grid size={{ xs: 12, md: 2.5 }}>
                        <AsyncAutocompleteElement
                          api={() => OriginAPI.select({ is_active: 1 })}
                          name={`modos.${index}.origin`}
                          label={t('Origin')}
                          fetchOnMount
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2.5 }}>
                        <AsyncAutocompleteElement
                          api={() =>
                            CauseAPI.select({
                              is_active: 1,
                              origin: form.getValues(`modos.${index}.origin`)
                                ?.id,
                              unit: data?.data?.[0]?.unit?.id || null,
                            })
                          }
                          name={`modos.${index}.cause`}
                          label={t('Cause')}
                          fetchOnMount
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: index > 0 ? 6.5 : 7 }}>
                        <TextFieldElement
                          name={`modos.${index}.comment`}
                          fullWidth
                          label={t('Comment')}
                        />
                      </Grid>
                      {index > 0 && (
                        <Grid size={{ xs: 12, md: 0.5 }}>
                          <Button
                            color="error"
                            onClick={() => remove(index)}
                            variant="contained"
                            sx={{
                              height: '100%',
                              background: '#242A3B',
                              width: '100%',
                              padding: 0,
                              minWidth: '40px',
                            }}
                          >
                            <img
                              src={DeleteIcon}
                              alt={t('Delete')}
                              style={{ width: '24px', height: '24px' }}
                            />
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  ))}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <Box display="flex" justifyContent="flex-end" mt={2} gap={3}>
                <Button
                  color={'error'}
                  variant="outlined"
                  onClick={() => navigate('/nnc/entries')}
                  sx={{ border: '1px solid #0093FF !important', color: '#000' }}
                >
                  {t('Cancel')}
                </Button>
                <Button variant="contained" color="primary" type="submit">
                  {t('Confirm')}
                </Button>
              </Box>
            </Stack>
          </FormContainer>
        )}
      </Box>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <Box
          sx={{
            width: '70vw',
            bgcolor: 'background.paper',
            p: 4,
            m: 'auto',
            mt: 10,
            maxHeight: '80vh',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" component="h2" mb={4}>
            {t('Select Entries for NNC Analysis')}
          </Typography>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', maxHeight: '50vh' }}
          >
            <DataGrid
              columns={[
                {
                  field: 'reference_number',
                  headerName: t('ID'),
                  width: 150,
                },
                {
                  field: 'nnc_date',
                  headerName: t('Date'),
                  width: 100,
                },
                {
                  field: 'division',
                  headerName: t('Identified Area'),
                  renderCell: (params: GridRenderCellParams) => {
                    return params?.row?.division?.label;
                  },
                  flex: 1,
                },
                {
                  field: 'machine_enrichment',
                  headerName: t('NNC Machine'),
                  renderCell: (params: GridRenderCellParams) => {
                    return params?.row?.machine_enrichment?.label;
                  },
                  flex: 1,
                },
                {
                  field: 'operator_name_enrichment',
                  headerName: t('Operator Generator'),
                  renderCell: (params: GridRenderCellParams) => {
                    return params?.row?.operator_name_enrichment?.label;
                  },
                  flex: 1,
                },
                {
                  field: 'comment',
                  headerName: t('NNC Comment'),
                  flex: 2,
                },
              ]}
              rows={modalData || []}
              checkboxSelection
              onRowSelectionModelChange={(newRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel);
              }}
              rowSelectionModel={rowSelectionModel}
              hideFooter
              sx={{
                '& .MuiDataGrid-columnHeaders > div': {
                  backgroundColor: '#FCFCFD !important',
                },
              }}
            />
          </Box>
          <Box display={'flex'} justifyContent="flex-end" mt={2}>
            <Button
              onClick={() => {
                setOpen(false);
              }}
              sx={{ mt: 2 }}
            >
              {t('Close')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default NNCAnalysisForm;
